import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { login, clearError, setRememberEmail, getRememberedEmail } from '@/store/authSlice';
import { initializeApi } from '@/api';
import { designTokens } from '@/utils/design-tokens';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import Checkbox from '@/components/ui/Checkbox';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated, rememberEmail, autoLogin } = useAppSelector(
    (state) => state.auth
  );

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Initialize API on component mount
  useEffect(() => {
    initializeApi();

    // Load remembered email if exists
    const rememberedEmail = getRememberedEmail();
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      dispatch(setRememberEmail(true));
    }
  }, [dispatch]);

  // Redirect if already authenticated when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard/mytask');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when user types (but not immediately)
  useEffect(() => {
    if (error && (email || password)) {
      console.log('Clearing error after user input change');
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 100); // Small delay to ensure error is shown first
      return () => clearTimeout(timer);
    }
  }, [email, password, dispatch]);

  // Debug: Log error state
  useEffect(() => {
    if (error) {
      console.log('Error state updated:', error);
    }
  }, [error]);

  // Form validation
  const validateForm = () => {
    let isValid = true;

    // Reset errors
    setEmailError('');
    setPasswordError('');

    // Email validation
    if (!email) {
      setEmailError('이메일을 입력해주세요.');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('올바른 이메일 형식을 입력해주세요.');
      isValid = false;
    }

    // Password validation
    if (!password) {
      setPasswordError('비밀번호를 입력해주세요.');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('비밀번호는 6자 이상 입력해주세요.');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(login({
        email,
        password,
        rememberEmail,
        autoLogin
      })).unwrap();
      navigate('/dashboard/mytask');
    } catch (error: any) {
      // The error is already set in Redux state by the rejected action
      console.error('❌ Login failed:', error);
      console.log('Error from Redux:', error);
      // The error message will be displayed through Redux error state
    }
  };

  const handleForgotPassword = () => {
    navigate('/reset-password');
  };

  return (
    <div className="w-full h-screen flex">
      {/* Left Sidebar */}
      <div className="w-[800px] flex-shrink-0" style={{ backgroundColor: designTokens.colors.primary.dark }} />

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col justify-center items-center gap-10" style={{ backgroundColor: designTokens.colors.gray[0] }}>
        {/* Login Card */}
        <div className="w-[500px] bg-white rounded-md p-[50px_40px_40px] shadow-sm">
          {/* Header */}
          <div className="flex justify-between items-center pb-5 mb-[15px]">
            <h1
              style={{
                fontSize: designTokens.typography.fontSize['title-large'][0],
                fontWeight: designTokens.typography.fontSize['title-large'][1].fontWeight,
                lineHeight: designTokens.typography.fontSize['title-large'][1].lineHeight,
                fontFamily: designTokens.typography.fontFamily.sans[0],
                color: designTokens.colors.black
              }}
            >
              로그인
            </h1>

            {/* Logo */}
            <img
              src="/images/logo-big.png"
              alt="DOT Logo"
              className="h-[33px] w-auto"
            />
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="email"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                    lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[6]
                  }}
                >
                  이메일
                </label>
                <span
                  style={{
                    fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                    fontWeight: designTokens.typography.fontSize['body-xsmall'][1].fontWeight,
                    lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.error.DEFAULT
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="이메일을 입력해주세요."
                size="large"
                state={emailError ? 'error' : 'default'}
                disabled={isLoading}
                style={{
                  height: '48px',
                  fontSize: '18px',
                  fontFamily: 'Pretendard GOV',
                  lineHeight: '1.193359375em'
                }}
              />

              {emailError && (
                <p
                  className="mt-1"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-small'][0],
                    color: designTokens.colors.error.DEFAULT,
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  {emailError}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="password"
                  className="text-base font-normal text-[#1B1B1B]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  비밀번호
                </label>
                <span
                  className="text-xs text-[#E53935]"
                  style={{
                    fontFamily: 'Pretendard GOV',
                    lineHeight: '1.193359375em'
                  }}
                >
                  *
                </span>
              </div>

              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력해주세요."
                size="large"
                state={passwordError ? 'error' : 'default'}
                disabled={isLoading}
                showPasswordToggle={true}
                showClearButton={false}
                style={{
                  height: '48px',
                  fontSize: '18px',
                  fontFamily: 'Pretendard GOV',
                  lineHeight: '1.193359375em'
                }}
              />

              {passwordError && (
                <p className="text-sm text-[#E53935] mt-1">{passwordError}</p>
              )}
            </div>

            {/* Checkboxes */}
            <div className="flex gap-5 py-2">
              <Checkbox
                id="rememberEmail"
                checked={rememberEmail}
                onChange={(checked) => dispatch(setRememberEmail(checked))}
                label="아이디 기억"
                size="medium"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div
                className="text-center py-2"
                style={{
                  fontSize: designTokens.typography.fontSize['body-small'][0],
                  color: designTokens.colors.error.DEFAULT,
                  fontFamily: designTokens.typography.fontFamily.sans[0]
                }}
              >
                {error}
              </div>
            )}

            {/* Submit Button and Forgot Password */}
            <div className="space-y-5 pt-[15px]">
              <Button
                type="submit"
                size="large"
                disabled={isLoading}
                className="w-full h-[48px] border-2 font-semibold text-xl cursor-pointer disabled:cursor-not-allowed"
                style={{
                  backgroundColor: designTokens.colors.primary.dark,
                  borderColor: designTokens.colors.primary.dark,
                  color: designTokens.colors.gray[0],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  lineHeight: designTokens.typography.fontSize['title-xsmall'][1].lineHeight
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = designTokens.colors.primary.DEFAULT
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = designTokens.colors.primary.dark
                }}
              >
                {isLoading ? '로그인 중...' : '로그인'}
              </Button>

              <button
                type="button"
                onClick={handleForgotPassword}
                className="w-full text-xs text-[#131416] text-center hover:underline"
                style={{
                  fontFamily: 'Pretendard GOV',
                  lineHeight: '1.193359375em'
                }}
                disabled={isLoading}
              >
                비밀번호를 잊으셨나요?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;